/**
 * PostHog Analytics for Hedra Widget
 *
 * Loads PostHog from CDN asynchronously to avoid bundle size impact.
 * Provides typed event tracking functions for widget events.
 */

// PostHog configuration
const POSTHOG_API_KEY = "phc_dVP24ZvRMavPXZDRO5v3lYsYOfEOp9GGg0sc6hHeDa9";
const POSTHOG_UI_HOST = "https://eu.posthog.com"; // For links to dashboard

// Proxy configuration - routes through Next.js rewrites to avoid ad blockers
const BASE_URL = process.env.NEXT_PUBLIC_BASE_URL ?? "";
const POSTHOG_HOST = `${BASE_URL}/ingest`; // Proxied API endpoint
const POSTHOG_SCRIPT_URL = `${BASE_URL}/ingest/static/array.js`; // Proxied script

// Global PostHog instance (loaded from CDN)
let posthog: PostHogInstance | null = null;
let isInitialized = false;

// Queue events until PostHog is loaded
const eventQueue: Array<{
  event: string;
  properties: Record<string, unknown>;
}> = [];

// Widget context (set during initialization)
let widgetContext: {
  widgetId: string;
  origin: string;
  sessionId: string;
} | null = null;

// PostHog instance type (minimal interface for what we use)
interface PostHogInstance {
  capture: (event: string, properties?: Record<string, unknown>) => void;
  identify: (distinctId: string, properties?: Record<string, unknown>) => void;
  reset: () => void;
}

// Declare global window.posthog
declare global {
  interface Window {
    posthog?: PostHogInstance;
  }
}

/**
 * Generate a unique session ID for this widget instance
 */
function generateSessionId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Load PostHog via proxy (routes through Next.js rewrites to avoid ad blockers)
 */
function loadPostHogScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    // Check if already loaded
    if (window.posthog) {
      posthog = window.posthog;
      resolve();
      return;
    }

    // Create script element - load from proxy to avoid ad blockers
    const script = document.createElement("script");
    script.src = POSTHOG_SCRIPT_URL;
    script.async = true;

    script.onload = () => {
      // Initialize PostHog after script loads
      if (window.posthog) {
        window.posthog.capture("$pageview");
        posthog = window.posthog;
        resolve();
      } else {
        reject(new Error("PostHog failed to initialize"));
      }
    };

    script.onerror = () => {
      console.warn("[Analytics] Failed to load PostHog script");
      reject(new Error("Failed to load PostHog script"));
    };

    // Add PostHog initialization snippet
    const initScript = document.createElement("script");
    initScript.textContent = `
      !function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.async=!0,p.src=s.api_host.replace(".i.posthog.com","-assets.i.posthog.com")+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+".people (stub)"},o="init capture register register_once register_for_session unregister unregister_for_session getFeatureFlag getFeatureFlagPayload isFeatureEnabled reloadFeatureFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey getNextSurveyStep identify setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException loadToolbar get_property getSessionProperty createPersonProfile opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing debug".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);
      posthog.init('${POSTHOG_API_KEY}', {
        api_host: '${POSTHOG_HOST}',
        person_profiles: 'always',
        capture_pageview: false,
        capture_pageleave: true,
        autocapture: true,
        session_recording: {
          maskAllInputs: false,
          maskTextSelector: null,
        },
      });
    `;

    document.head.appendChild(initScript);
    document.head.appendChild(script);

    // Give it a moment to initialize
    setTimeout(() => {
      if (window.posthog) {
        posthog = window.posthog;
        resolve();
      }
    }, 100);
  });
}

/**
 * Process queued events after PostHog loads
 */
function processEventQueue(): void {
  if (!posthog) return;

  while (eventQueue.length > 0) {
    const item = eventQueue.shift();
    if (item) {
      posthog.capture(item.event, item.properties);
    }
  }
}

/**
 * Initialize analytics for the widget
 */
export async function initAnalytics(widgetId: string): Promise<void> {
  if (isInitialized) {
    console.warn("[Analytics] Already initialized");
    return;
  }

  // Set widget context
  widgetContext = {
    widgetId,
    origin: window.location.origin,
    sessionId: generateSessionId(),
  };

  try {
    await loadPostHogScript();

    if (posthog && widgetContext) {
      // Identify this widget session
      posthog.identify(widgetContext.sessionId, {
        widgetId: widgetContext.widgetId,
        origin: widgetContext.origin,
      });

      // Process any queued events
      processEventQueue();

      isInitialized = true;
      console.log(
        "[Analytics] PostHog initialized for widget:",
        widgetContext.widgetId
      );
    }
  } catch (error) {
    console.warn("[Analytics] Failed to initialize PostHog:", error);
    // Analytics failure should not break the widget
  }
}

/**
 * Track an event with properties
 */
export function trackEvent(
  event: string,
  properties?: Record<string, unknown>
): void {
  const fullProperties = {
    ...properties,
    ...(widgetContext && {
      widgetId: widgetContext.widgetId,
      origin: widgetContext.origin,
      sessionId: widgetContext.sessionId,
    }),
  };

  if (posthog) {
    posthog.capture(event, fullProperties);
  } else {
    // Queue event if PostHog not loaded yet
    eventQueue.push({ event, properties: fullProperties });
  }
}

// Typed event tracking functions

export function trackWidgetInitialized(): void {
  trackEvent("widget_initialized");
}

export function trackConversationStarted(): void {
  trackEvent("conversation_started", {
    timestamp: Date.now(),
  });
}

export function trackConversationEnded(
  durationMs: number,
  messageCount: number,
  reason: "user_stopped" | "timeout" | "error"
): void {
  trackEvent("conversation_ended", {
    durationMs,
    messageCount,
    reason,
  });
}

export function trackTranscriptionComplete(
  durationMs: number,
  charCount: number,
  isEmpty: boolean
): void {
  trackEvent("transcription_complete", {
    durationMs,
    charCount,
    isEmpty,
  });
}

export function trackChatResponseReceived(
  durationMs: number,
  responseLength: number
): void {
  trackEvent("chat_response_received", {
    durationMs,
    responseLength,
  });
}

export function trackAvatarSpeaking(textLength: number): void {
  trackEvent("avatar_speaking", {
    textLength,
  });
}

export function trackAvatarConnected(connectionTimeMs: number): void {
  trackEvent("avatar_connected", {
    connectionTimeMs,
  });
}

export function trackAvatarDisconnected(reason: string): void {
  trackEvent("avatar_disconnected", {
    reason,
  });
}

export function trackError(
  errorType: string,
  stage: string,
  message: string
): void {
  trackEvent("error_occurred", {
    errorType,
    stage,
    message,
  });
}

export function trackUserSpeechDetected(vadAvailable: boolean): void {
  trackEvent("user_speech_detected", {
    vadAvailable,
  });
}
